import React, { useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "../hooks/useTheme";
import { useAppSelector } from "../store/hooks";
import { StreakSparkIcon } from "./svg-icons/streak-spark";

type AppSidebarProps = {
  className?: string;
  showQuickActions?: boolean;
  showThemeToggle?: boolean;
  showUserCard?: boolean;
};

const getInitials = (
  name?: string | null,
  username?: string | null,
): string => {
  if (name) {
    return name
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase())
      .slice(0, 2)
      .join("");
  }
  if (username) return username.charAt(0).toUpperCase();
  return "U";
};

const AppSidebar: React.FC<AppSidebarProps> = ({
  className = "",
  showQuickActions = true,
  showThemeToggle = true,
  showUserCard = true,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { isDark, setTheme } = useTheme();

  const streakSpark = useAppSelector((s) => s.user.streakSpark);
  const userProfile = useAppSelector((s) => s.user.profile);
  const initials = getInitials(userProfile?.name, userProfile?.username);

  const isNavActive = useCallback(
    (path: string) =>
      location.pathname === path || location.pathname.startsWith(path + "/"),
    [location.pathname],
  );

  const navLinks = [
    { path: "/app", emoji: "\u{1F4C5}", label: "Home" },
    { path: "/habits", emoji: "\u{1F525}", label: t("nav.habits") },
    { path: "/todos", emoji: "\u2705", label: t("nav.todos") },
    { path: "/notes", emoji: "\u{1F4DD}", label: t("nav.notes") },
    { path: "/trackers", emoji: "\u{1F4CA}", label: t("nav.trackers") },
  ];

  const handleThemeToggle = () => {
    setTheme(isDark ? "light" : "dark");
  };

  return (
    <aside
      className={`hidden lg:flex flex-col w-64 xl:w-72 shrink-0 bg-white dark:bg-[#0f0f12] border-r border-black/[0.07] dark:border-white/[0.07] min-h-screen sticky top-0 ${className}`}
    >
      <div className="flex items-center gap-3 px-5 py-5 border-b border-black/[0.06] dark:border-white/[0.07]">
        <button
          onClick={() => navigate("/")}
          aria-label="Go to home page"
          className="w-9 h-9 rounded-xl bg-blue-500/75 opacity-75 flex items-center justify-center text-xl shrink-0 hover:opacity-100 transition-opacity"
        >
          <StreakSparkIcon />
        </button>
        <span className="text-[21px] font-semibold tracking-tight text-gray-900 dark:text-white">
          {t("app.name")}
        </span>
      </div>

      {showUserCard && (
        <button
          onClick={() => navigate("/profile")}
          className="flex items-center gap-3 px-5 py-4 border-b border-black/[0.06] dark:border-white/[0.07] hover:bg-black/[0.03] dark:hover:bg-white/[0.04] transition-colors text-left w-full"
        >
          <div className="w-10 h-10 rounded-full bg-[#3b5cde] flex items-center justify-center text-[17px] font-semibold text-white shrink-0">
            {initials}
          </div>
          <div>
            <p className="text-[17px] font-medium text-gray-900 dark:text-white leading-none">
              {userProfile?.name || userProfile?.username || "User"}
            </p>
            <p className="text-[15px] text-gray-400 dark:text-white/40 mt-0.5">
              {streakSpark?.totalSparkPoints ?? 0} pts
            </p>
          </div>
        </button>
      )}

      <nav className="flex flex-col gap-1 px-3 py-4 flex-1">
        {navLinks.map(({ path, emoji, label }) => {
          const active = isNavActive(path);
          return (
            <button
              key={path}
              onClick={() => navigate(path)}
              className={`side-link flex items-center gap-3 px-3 py-2.5 rounded-xl text-[17px] font-medium transition-all duration-150 w-full text-left${
                active
                  ? " active"
                  : " hover:bg-black/[0.04] dark:hover:bg-white/[0.05] text-gray-500 dark:text-white/50"
              }`}
            >
              <span
                className={`side-icon w-7 h-7 rounded-lg flex items-center justify-center text-sm${active ? "" : " bg-white/[0.06] dark:bg-white/[0.06] bg-black/[0.04]"}`}
              >
                {emoji}
              </span>
              <span
                className={
                  active ? "text-[#f5a623]" : "text-gray-500 dark:text-white/50"
                }
              >
                {label}
              </span>
            </button>
          );
        })}
      </nav>

      {showQuickActions && (
        <div className="px-3 py-4 border-t border-black/[0.06] dark:border-white/[0.07]">
          <p className="text-[13px] font-medium uppercase tracking-widest text-gray-400 dark:text-white/25 px-2 mb-2">
            {t("quickActions.title")}
          </p>
          <div className="grid grid-cols-5 gap-1.5">
            <button
              onClick={() => navigate("/add-todo")}
              className="tap-scale flex flex-col items-center gap-1.5 p-2 rounded-xl border border-black/[0.07] dark:border-white/[0.08] bg-white dark:bg-white/[0.03] hover:bg-black/[0.05] dark:hover:bg-white/[0.08] transition-all duration-150 cursor-pointer"
            >
              <div className="w-7 h-7 rounded-lg bg-[#3b5cde]/20 flex items-center justify-center">
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                  <rect
                    x="1"
                    y="2"
                    width="14"
                    height="2"
                    rx="1"
                    fill="#378ADD"
                  />
                  <rect
                    x="1"
                    y="7"
                    width="10"
                    height="2"
                    rx="1"
                    fill="#378ADD"
                  />
                  <rect
                    x="1"
                    y="12"
                    width="12"
                    height="2"
                    rx="1"
                    fill="#378ADD"
                  />
                </svg>
              </div>
              <span className="text-[12px] font-medium text-gray-400 dark:text-white/40">
                {t("quickActions.todo")}
              </span>
            </button>

            <button
              onClick={() => navigate("/add-habit")}
              className="tap-scale flex flex-col items-center gap-1.5 p-2 rounded-xl border border-black/[0.07] dark:border-white/[0.08] bg-white dark:bg-white/[0.03] hover:bg-black/[0.05] dark:hover:bg-white/[0.08] transition-all duration-150 cursor-pointer"
            >
              <div className="w-7 h-7 rounded-lg bg-[#f5a623]/20 flex items-center justify-center">
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                  <circle
                    cx="8"
                    cy="8"
                    r="6"
                    stroke="#f5a623"
                    strokeWidth="2"
                  />
                  <path
                    d="M5 8l2 2 4-4"
                    stroke="#f5a623"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <span className="text-[12px] font-medium text-gray-400 dark:text-white/40">
                {t("quickActions.habit")}
              </span>
            </button>

            <button
              onClick={() => navigate("/add-reminder")}
              className="tap-scale flex flex-col items-center gap-1.5 p-2 rounded-xl border border-black/[0.07] dark:border-white/[0.08] bg-white dark:bg-white/[0.03] hover:bg-black/[0.05] dark:hover:bg-white/[0.08] transition-all duration-150 cursor-pointer"
            >
              <div className="w-7 h-7 rounded-lg bg-[#e23f5d]/20 flex items-center justify-center">
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M8 2a4 4 0 0 0-4 4v3l-1 1h10l-1-1V6a4 4 0 0 0-4-4z"
                    fill="#E23F5D"
                  />
                  <rect
                    x="6.5"
                    y="12"
                    width="3"
                    height="1.5"
                    rx="0.75"
                    fill="#E23F5D"
                  />
                </svg>
              </div>
              <span className="text-[12px] font-medium text-gray-400 dark:text-white/40">
                {t("quickActions.remind")}
              </span>
            </button>

            <button
              onClick={() => navigate("/add-note")}
              className="tap-scale flex flex-col items-center gap-1.5 p-2 rounded-xl border border-black/[0.07] dark:border-white/[0.08] bg-white dark:bg-white/[0.03] hover:bg-black/[0.05] dark:hover:bg-white/[0.08] transition-all duration-150 cursor-pointer"
            >
              <div className="w-7 h-7 rounded-lg bg-[#22c67a]/20 flex items-center justify-center">
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                  <rect
                    x="2"
                    y="1"
                    width="12"
                    height="14"
                    rx="2"
                    stroke="#22c67a"
                    strokeWidth="1.8"
                  />
                  <path
                    d="M5 5h6M5 8h4M5 11h5"
                    stroke="#22c67a"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <span className="text-[12px] font-medium text-gray-400 dark:text-white/40">
                {t("quickActions.note")}
              </span>
            </button>

            <button
              onClick={() => navigate("/add-tracker")}
              className="tap-scale flex flex-col items-center gap-1.5 p-2 rounded-xl border border-black/[0.07] dark:border-white/[0.08] bg-white dark:bg-white/[0.03] hover:bg-black/[0.05] dark:hover:bg-white/[0.08] transition-all duration-150 cursor-pointer"
            >
              <div className="w-7 h-7 rounded-lg bg-[#a33fe8]/20 flex items-center justify-center">
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M2 12l3-4 3 2 3-5 3 3"
                    stroke="#a33fe8"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <span className="text-[12px] font-medium text-gray-400 dark:text-white/40">
                {t("quickActions.track")}
              </span>
            </button>
          </div>
        </div>
      )}

      {showThemeToggle && (
        <div className="px-5 py-4 border-t border-black/[0.06] dark:border-white/[0.07]">
          <button
            onClick={handleThemeToggle}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl border border-black/[0.07] dark:border-white/[0.08] bg-white dark:bg-white/[0.03] hover:bg-black/[0.04] dark:hover:bg-white/[0.07] transition-all cursor-pointer text-left"
          >
            <span className="text-[19px]">
              {isDark ? "\u2600\uFE0F" : "\u{1F319}"}
            </span>
            <span className="text-[16px] font-medium text-gray-500 dark:text-white/50">
              {isDark ? t("theme.switchToLight") : t("theme.switchToDark")}
            </span>
          </button>
        </div>
      )}
    </aside>
  );
};

export default AppSidebar;
